import { ToggleViewButtons } from '@components/toggle-view-buttons/ToggleViewButton';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import React, { useEffect } from 'react';
import { useExportModal } from '@hooks/export/export-modal/UseExportModal';
import { EmptyTabsPage, EnabledExportPages, ViewType } from '@hooks/export/Model';
import { PerformanceAnalysisViewMapping } from '@components/performance-analysis-card/Types';
import { getDisplayNameFromHeaderKey } from '@models/PerformanceAnalysisboard';
import {
  dataExportInitiatedTrackingFunction,
  DataExportTrackingElementKpiAndSortParam,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { WithExportModal } from '@components/design';

export interface PerformanceAnalysisHeaderIconsProps {
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  view: ViewType;
  setIsExportModalOpen: (isExportModalOpen: boolean) => void;
  isExportButtonDisabled: boolean;
  volume: string;
  onChange: (view: ViewType) => void;
}

export const PerformanceAnalysisHeaderIcons: React.FC<PerformanceAnalysisHeaderIconsProps> = ({
  view,
  onChartDownload,
  setIsExportModalOpen,
  isExportButtonDisabled,
  volume,
  onChange,
}) => {
  const pagesConfig = usePageConfig();
  const viewType = (() => {
    if (view === ViewType.Table) return ViewType.Table;
    if (view === ViewType.BarChart) return ViewType.BarChart;
    if (view === ViewType.LineChart) return ViewType.LineChart;
    if (view === ViewType.StackedChart) return ViewType.StackedChart;
    return ViewType.Table;
  })();

  const {
    openExportModal,
    onCloseExportModal,
    onExportError,
    exportModalFormats,
    exportModalMessage,
    exportModalHeaderText,
    isExportModalOpen,
  } = useExportModal({
    currentPage: EnabledExportPages.PerformanceAnalysis,
    currentTab: EmptyTabsPage.None,
    currentView: viewType,
  });
  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');

  const getElementKpiAndSortParam = (): DataExportTrackingElementKpiAndSortParam => {
    if (ViewType.LineChart === view) {
      return {};
    }

    if (ViewType.StackedChart === view) {
      return {
        elementKpi: `${getDisplayNameFromHeaderKey(pagesConfig.sortingOptions.headerKey, volume)}`,
      };
    }

    return {
      elementKpi: getDisplayNameFromHeaderKey(pagesConfig.sortingOptions.headerKey, volume),
      elementSort: pagesConfig.sortingOptions,
    };
  };

  const handleExportTracking = () => {
    const dataExportInitiatedTrackingParam: DataExportTrackingElementParam = {
      elementType: PerformanceAnalysisViewMapping[view],
      ...getElementKpiAndSortParam(),
    };
    handleDataExportTrackingFunction(dataExportInitiatedTrackingParam, dataExportInitiatedTrackingFunction);
  };

  const onExportClick = () => {
    handleExportTracking();
    openExportModal();
  };

  useEffect(() => {
    if (isExportModalOpen) {
      setIsExportModalOpen(isExportModalOpen);
    }
  }, [isExportModalOpen]);

  return (
    <>
      <ToggleViewButtons
        {...{
          view: viewType,
          isExportDisabled: isExportButtonDisabled,
          onExportClick,
          onChange,
        }}
      />
      <WithExportModal
        isShown={isExportModalOpen}
        exportFormats={exportModalFormats}
        message={exportModalMessage}
        title={exportModalHeaderText}
        download={onChartDownload}
        onClose={onCloseExportModal}
        onError={onExportError}
      />
    </>
  );
};
